import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, Download, FileSpreadsheet } from 'lucide-react';
import { ExportJob } from '@/types/import';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ExportProgressProps {
  jobId: string;
  getJobStatus: (jobId: string) => Promise<ExportJob>;
  onDownload: (jobId: string) => void;
  onClose: () => void;
}

export const ExportProgress: React.FC<ExportProgressProps> = ({
  jobId,
  getJobStatus,
  onDownload,
  onClose,
}) => {
  const [job, setJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Poll for job status
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let mounted = true;

    const fetchStatus = async () => {
      try {
        const status = await getJobStatus(jobId);
        if (mounted) {
          setJob(status);

          // Stop polling when complete or failed
          if (['COMPLETED', 'FAILED'].includes(status.status)) {
            clearInterval(intervalId);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch job status');
          clearInterval(intervalId);
        }
      }
    };

    // Initial fetch
    fetchStatus();

    // Poll every 2 seconds
    intervalId = setInterval(fetchStatus, 2000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [jobId, getJobStatus]);

  const getStatusIcon = () => {
    if (!job) return <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />;

    switch (job.status) {
      case 'COMPLETED':
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-8 h-8 text-red-600" />;
      default:
        return <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (!job) return 'Initializing export...';

    switch (job.status) {
      case 'PENDING':
        return 'Preparing export...';
      case 'PROCESSING':
        return 'Generating file...';
      case 'COMPLETED':
        return 'Export complete!';
      case 'FAILED':
        return 'Export failed';
      default:
        return 'Processing...';
    }
  };

  const isComplete = job?.status === 'COMPLETED';
  const isFailed = job?.status === 'FAILED';

  return (
    <div className="space-y-6 text-center py-4">
      {/* Status icon */}
      <div className="flex justify-center">
        <div className={cn(
          'w-16 h-16 rounded-full flex items-center justify-center',
          isComplete ? 'bg-green-100' :
          isFailed ? 'bg-red-100' :
          'bg-blue-100'
        )}>
          {getStatusIcon()}
        </div>
      </div>

      {/* Status text */}
      <div>
        <h4 className="text-lg font-medium text-gray-900">{getStatusText()}</h4>
        {job && !isComplete && !isFailed && (
          <p className="text-sm text-gray-500 mt-1">
            Exporting {job.totalRecords.toLocaleString()} records...
          </p>
        )}
        {isComplete && job && (
          <p className="text-sm text-gray-500 mt-1">
            {job.totalRecords.toLocaleString()} records exported as {job.format.toUpperCase()}
          </p>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-center space-x-3">
        {isComplete && (
          <Button
            onClick={() => onDownload(jobId)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Download File
          </Button>
        )}

        <Button variant="outline" onClick={onClose}>
          {isComplete ? 'Done' : 'Cancel'}
        </Button>
      </div>

      {/* File type indicator */}
      {job && (
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
          <FileSpreadsheet className="w-4 h-4" />
          <span>{job.format === 'csv' ? 'CSV File' : 'Excel File (.xlsx)'}</span>
        </div>
      )}
    </div>
  );
};
